import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, MessageCircle, Mail, Phone, Clock, ChevronDown, Check, CheckCheck } from 'lucide-react';
import communicationService from '../../services/communicationService';
import SendMessageForm from './SendMessageForm';

const CommunicationThread = ({ thread, onMessageSent, onRefresh }) => {
  const [messages, setMessages] = useState(thread.messages || []);
  const [showSendForm, setShowSendForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Quick reply state
  const [quickReplyContent, setQuickReplyContent] = useState('');
  const [quickReplySubject, setQuickReplySubject] = useState('');
  const [quickReplyType, setQuickReplyType] = useState(null); // null means auto-detect
  const [sendingQuickReply, setSendingQuickReply] = useState(false);
  const [quickReplyError, setQuickReplyError] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const quickReplyTextareaRef = useRef(null);

  const loadClientInfo = useCallback(async () => {
    try {
      const api = await import('../../services/api');
      const response = await api.default.get(`/clients/${thread.client_id}`);
      const client = response.data;
      setClientPhone(client.phone || '');
      setClientEmail(client.email || '');
    } catch (error) {
      console.error('Error loading client info:', error);
    }
  }, [thread.client_id]);

  useEffect(() => {
    setMessages(thread.messages || []);
    // Clear quick reply form when switching threads
    setQuickReplyContent('');
    setQuickReplySubject('');
    setQuickReplyType(null); // Reset to auto-detect
    setQuickReplyError('');
    // Load client contact information
    loadClientInfo();
    // Scroll to bottom immediately when switching threads
    setTimeout(() => scrollToBottom(true), 0);
  }, [thread, loadClientInfo]);

  const scrollToBottom = (immediate = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: immediate ? 'auto' : 'smooth' });
  };

  const loadMoreMessages = async () => {
    try {
      setLoading(true);
      const response = await communicationService.getCommunications({
        client_id: thread.client_id,
        case_id: thread.case_id,
        page: Math.ceil(messages.length / 20) + 1,
        size: 20,
      });
      
      setMessages(prev => [...response.communications, ...prev]);
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (messageData) => {
    try {
      let response;
      if (messageData.type === 'sms') {
        response = await communicationService.sendSMS({
          client_id: thread.client_id,
          case_id: thread.case_id,
          content: messageData.content,
          to_phone: messageData.to_phone,
        });
      } else if (messageData.type === 'email') {
        response = await communicationService.sendEmail({
          client_id: thread.client_id,
          case_id: thread.case_id,
          subject: messageData.subject,
          content: messageData.content,
          to_email: messageData.to_email,
        });
      }
      
      // Add the new message to the thread
      const newMessage = {
        ...response,
        user_name: 'You',
        client_name: thread.client_name,
        case_title: thread.case_title,
        direction: 'outbound'
      };
      
      setMessages(prev => [newMessage, ...prev]);
      setShowSendForm(false);
      
      if (onMessageSent) {
        onMessageSent();
      }
      
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      // Error handling is done in the SendMessageForm component
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const getDateSeparatorText = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'sms':
        return <MessageCircle className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'phone_call':
        return <Phone className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Check className="w-4 h-4" title="Sent" />;
      case 'delivered':
        return <CheckCheck className="w-4 h-4" title="Delivered" />;
      case 'read':
        return <CheckCheck className="w-4 h-4 text-blue-400" title="Read" />;
      case 'failed':
        return <span className="text-red-400" title="Failed to send">✕</span>;
      default:
        return null;
    }
  };

  // Get the last inbound message to determine reply type
  const getLastInboundMessage = () => {
    return messages.find(msg => msg.direction === 'inbound');
  };

  // Determine message type for quick reply (defaults to last inbound message type)
  const getQuickReplyType = () => {
    // If user manually selected a type, use that
    if (quickReplyType !== null) {
      return quickReplyType;
    }
    // Otherwise, auto-detect from last inbound message
    const lastInbound = getLastInboundMessage();
    return lastInbound?.type || 'sms'; // Default to SMS if no inbound messages
  };

  // Get recipient info for quick reply
  const getQuickReplyRecipient = () => {
    const lastInbound = getLastInboundMessage();
    if (!lastInbound) return null;

    if (lastInbound.type === 'sms') {
      return { type: 'sms', address: lastInbound.from_address };
    } else if (lastInbound.type === 'email') {
      return { type: 'email', address: lastInbound.from_address };
    }
    return null;
  };

  // Handle quick reply submission
  const handleQuickReply = async () => {
    if (!quickReplyContent.trim()) return;

    setQuickReplyError('');
    setSendingQuickReply(true);

    try {
      const messageType = getQuickReplyType();
      const recipient = getQuickReplyRecipient();

      let response;
      if (messageType === 'sms') {
        // Only use recipient address if it looks like a phone number (not an email)
        const recipientIsPhone = recipient?.address && !recipient.address.includes('@');
        const phoneToUse = (recipientIsPhone ? recipient.address : null) || clientPhone;
        if (!phoneToUse) {
          throw new Error('No phone number available for this client');
        }
        response = await communicationService.sendSMS({
          client_id: thread.client_id,
          case_id: thread.case_id,
          content: quickReplyContent.trim(),
          to_phone: phoneToUse,
        });
      } else if (messageType === 'email') {
        if (!quickReplySubject.trim()) {
          throw new Error('Email subject is required');
        }
        // Only use recipient address if it looks like an email (not a phone number)
        const recipientIsEmail = recipient?.address && recipient.address.includes('@');
        const emailToUse = (recipientIsEmail ? recipient.address : null) || clientEmail;
        if (!emailToUse) {
          throw new Error('No email address available for this client');
        }
        response = await communicationService.sendEmail({
          client_id: thread.client_id,
          case_id: thread.case_id,
          subject: quickReplySubject.trim(),
          content: quickReplyContent.trim(),
          to_email: emailToUse,
        });
      }

      // Add the new message to the thread
      const newMessage = {
        ...response,
        user_name: 'You',
        client_name: thread.client_name,
        case_title: thread.case_title,
        direction: 'outbound',
        type: messageType,
      };

      setMessages(prev => [newMessage, ...prev]);
      setQuickReplyContent('');
      setQuickReplySubject('');

      if (onMessageSent) {
        onMessageSent();
      }

      scrollToBottom();
    } catch (error) {
      console.error('Error sending quick reply:', error);
      setQuickReplyError(error.message || 'Failed to send message');
    } finally {
      setSendingQuickReply(false);
    }
  };

  // Handle keyboard shortcuts for quick reply
  const handleQuickReplyKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleQuickReply();
    }
  };

  const getCharacterLimit = (type) => {
    // SMS: Twilio supports up to 1600 chars with automatic segmentation
    // Email: Much more flexible, set high limit
    return type === 'sms' ? 1600 : 50000;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm h-full flex flex-col">
      {/* Thread Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{thread.client_name}</h2>
            {thread.case_title && (
              <p className="text-sm text-gray-500">Case: {thread.case_title}</p>
            )}
          </div>

          {/* Only show "Send Message" button when no messages exist yet */}
          {messages.length === 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowSendForm(!showSendForm)}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send Message
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Send Message Form */}
      {showSendForm && (
        <div className="p-4 border-b bg-gray-50">
          <SendMessageForm
            clientId={thread.client_id}
            caseId={thread.case_id}
            clientName={thread.client_name}
            onSend={handleSendMessage}
            onCancel={() => setShowSendForm(false)}
          />
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-96">
        {/* Load More Button */}
        {messages.length >= 20 && (
          <div className="text-center">
            <button
              onClick={loadMoreMessages}
              disabled={loading}
              className="text-yellow-600 hover:text-gray-800 text-sm flex items-center gap-1 mx-auto"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              Load earlier messages
            </button>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No messages yet</p>
            <p className="text-sm">Start a conversation with this client</p>
          </div>
        ) : (
          messages.slice().reverse().map((message, index, reversedMessages) => {
            const showDateSeparator = index === 0 ||
              new Date(reversedMessages[index - 1]?.created_at).toDateString() !==
              new Date(message.created_at).toDateString();

            return (
              <div key={message.id || index}>
                {showDateSeparator && (
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-xs text-gray-500 px-2 font-medium">
                      {getDateSeparatorText(message.created_at)}
                    </span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                )}
                <div
                  className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg relative ${
                      message.direction === 'outbound'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {/* Message Type Badge */}
                    <div className={`absolute -top-2 ${message.direction === 'outbound' ? 'right-2' : 'left-2'}`}>
                      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        message.type === 'sms'
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-gray-100 text-yellow-600 border border-gray-200'
                      }`}>
                        {getMessageTypeIcon(message.type)}
                        <span>{message.type === 'sms' ? 'SMS' : 'Email'}</span>
                      </div>
                    </div>

                    {/* Message Header */}
                    <div className="flex items-center gap-2 mb-1 mt-2">
                      <span className="text-xs opacity-75">
                        {message.direction === 'outbound' ? message.user_name || 'You' : thread.client_name}
                      </span>
                      <span className="text-xs opacity-75">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatMessageTime(message.created_at)}
                      </span>
                    </div>

                    {/* Message Content */}
                    {message.subject && (
                      <div className="font-medium text-sm mb-1">
                        {message.subject}
                      </div>
                    )}

                    <div className="text-sm whitespace-pre-wrap">
                      {message.content}
                    </div>

                    {/* Message Status */}
                    {message.direction === 'outbound' && message.status && (
                      <div className="flex items-center gap-1 mt-2 opacity-75">
                        {getStatusIcon(message.status)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Reply Section */}
      {messages.length > 0 && (
        <div className="border-t bg-gray-50 p-4">
          {quickReplyError && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-2">
              <p className="text-sm text-red-700">{quickReplyError}</p>
            </div>
          )}

          {/* Message Type Toggle */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                setQuickReplyType('sms');
                setQuickReplySubject(''); // Clear subject when switching to SMS
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                getQuickReplyType() === 'sms'
                  ? 'bg-green-50 border-green-300 text-green-700 font-medium'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              SMS
            </button>
            <button
              type="button"
              onClick={() => setQuickReplyType('email')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                getQuickReplyType() === 'email'
                  ? 'bg-gray-50 border-blue-300 text-yellow-600 font-medium'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
          </div>

          {getQuickReplyType() === 'email' && (
            <input
              type="text"
              value={quickReplySubject}
              onChange={(e) => setQuickReplySubject(e.target.value)}
              placeholder="Email subject..."
              className="w-full p-2 mb-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
            />
          )}

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={quickReplyTextareaRef}
                value={quickReplyContent}
                onChange={(e) => setQuickReplyContent(e.target.value)}
                onKeyDown={handleQuickReplyKeyDown}
                placeholder={`Type a ${getQuickReplyType()} reply... (Ctrl+Enter to send)`}
                rows={getQuickReplyType() === 'email' ? 4 : 2}
                maxLength={getCharacterLimit(getQuickReplyType())}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none text-sm"
                disabled={sendingQuickReply}
              />

              <div className="flex justify-between items-center mt-1">
                <div className="flex items-center gap-2">
                  {(getQuickReplyRecipient()?.address || (getQuickReplyType() === 'sms' ? clientPhone : clientEmail)) && (
                    <span className="text-xs text-gray-600">
                      To: {getQuickReplyRecipient()?.address || (getQuickReplyType() === 'sms' ? clientPhone : clientEmail)}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {getQuickReplyType() === 'sms' && quickReplyContent.length > 160 && (
                    <span className="text-xs text-amber-600" title="Long messages are automatically split into segments (153 chars each).">
                      {Math.ceil(quickReplyContent.length / 153)} segments
                    </span>
                  )}
                  <span className={`text-xs ${
                    quickReplyContent.length > getCharacterLimit(getQuickReplyType()) * 0.9
                      ? 'text-red-600'
                      : 'text-gray-500'
                  }`}>
                    {quickReplyContent.length} / {getCharacterLimit(getQuickReplyType())}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleQuickReply}
              disabled={
                !quickReplyContent.trim() ||
                sendingQuickReply ||
                (getQuickReplyType() === 'email' && !quickReplySubject.trim()) ||
                (getQuickReplyType() === 'sms' && !clientPhone && !getQuickReplyRecipient()?.address) ||
                (getQuickReplyType() === 'email' && !clientEmail && !getQuickReplyRecipient()?.address)
              }
              className="self-start bg-yellow-500 text-white p-3 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              title={
                (getQuickReplyType() === 'sms' && !clientPhone && !getQuickReplyRecipient()?.address)
                  ? 'No phone number available for this client'
                  : (getQuickReplyType() === 'email' && !clientEmail && !getQuickReplyRecipient()?.address)
                  ? 'No email address available for this client'
                  : 'Send message (Ctrl+Enter)'
              }
            >
              {sendingQuickReply ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationThread;